// src/taskpane/App.tsx
import * as React from "react";
import { createRoot } from "react-dom/client";
import StreamingChatPanel from "../components/chat/StreamingChatPanel";
import { SessionProvider } from "../contexts/SessionContext";
import { ModelProvider } from "../contexts/ModelContext";
import { ChatSettingsProvider } from "../contexts/ChatSettingsContext";
import { AgentProvider, useAgent } from "../contexts/AgentContext";
import { Agent } from "../components/chat/AgentSelector";
import { useDocumentContext } from "../hooks/useDocumentContext";
import { useDocumentClassification } from "../hooks/useDocumentClassification";
import { useSession } from "../contexts/SessionContext";
import { createLogger } from "../utils/logger";
import { agentModelServiceFactory } from "../services/agentModelServiceFactory";
import { AuthProvider, useAuth } from "../contexts/AuthContext";
import {
  BlockingNoOrgScreen,
  BlockingNoUserScreen,
} from "../components/blocking/BlockingAccessScreens";
import { ErrorBoundary } from "../components/ErrorBoundary";

// NEW IMPORTS: Add these for file upload functionality
import { useFileUpload } from "../hooks/useFileUpload";
import { AgentAwareModelService } from "../services/agentAwareModelService";
import { useDragDropZone } from "../hooks/useDragDropZone";
import DragDropOverlay from "../components/chat/DragDropOverlay";
import ErrorMessage from "../components/chat/ErrorMessage";
import InfoBanner from "../components/chat/InfoBanner";
import LicenceExpiryBanner from "../components/chat/LicenceExpiryBanner";
import { validateFiles } from "../utils/fileUtils";
import { useTrackChangesAutoEnable } from "../hooks/useTrackChangesAutoEnable";
import {
  TrackChangesRevertNoticeProvider,
  useTrackChangesRevertNotice,
} from "../contexts/TrackChangesRevertNoticeContext";

// Import agent icons
import GeneralAgentSvg from "../../assets/GeneralAgent.svg";
import AgentRedLineAnalyzerSvg from "../../assets/AgentRedLineAnalyzer.svg";
import AgentContractQASvg from "../../assets/AgentContractQA.svg";
import AgentDraftingAssistantSvg from "../../assets/AgentDraftingAssistant.svg";
import { initAnalytics, posthogClient, setAnalyticsContext } from "../utils/analytics";
import { initNetworkMonitor } from "../utils/networkMonitor";
import { PostHogProvider } from "@posthog/react";

initAnalytics();
initNetworkMonitor();
// Chat is the task pane's only surface, so the ambient view never changes.
// Set once here so every event carries it.
setAnalyticsContext({ active_view: "chat" });

const log = createLogger("App");
// Define available agents at the App level
// This data is centralized here and passed to the AgentProvider
const AVAILABLE_AGENTS: Agent[] = [
  {
    id: "ccm-space",
    name: "General CCM Space",
    description: "Evaluate Bids, Experiment and more",
    icon: GeneralAgentSvg,
    tooltip:
      "Perfect when:\n" +
      "• Evaluating Bid against technical and commercial criteria.\n" +
      "• Experimenting with new CCM use-cases or questions\n" +
      "• Needing flexible output not confined to (existing) templates.\n" +
      "• Navigating unforeseen scenarios across the contract lifecycle\n" +
      '• Serving as a "Center of Excellence" advisor for general questions',
  },
  {
    id: "red-line-analyzer",
    name: "Redline Analyzer",
    description: "Review, Risk Assess & Negotiate Redlines",
    icon: AgentRedLineAnalyzerSvg,
    tooltip:
      "Perfect when:\n" +
      "• Vendors return MSAs with complex indemnity/liability redlines\n" +
      "• You need rapid RISK assessment (🔴🟡🟢) for COMPLIANCE\n" +
      "• Managing change requests against your negotiation playbook\n" +
      "• Protecting against knock-for-knock regime shifts\n" +
      "• Building negotiation strategies for high-value contracts",
  },
  {
    id: "contract-qa",
    name: "Contract Q&A",
    description: "Analyze, Compare & Query Contracts",
    icon: AgentContractQASvg,
    tooltip:
      "Perfect when:\n" +
      "• Comparing Supplier Paper against your clause library (template)\n" +
      "• Comparing post-signature MSAs, SCTs, etc. against templates\n" +
      "• Assisting negotiating Contract Draft (pre-award)\n" +
      "• Extracting obligations & compliance (post-award)",
  },
  {
    id: "drafting-assistant",
    name: "Drafting Assistant",
    description: "Draft Bid Strategy, SoWs, RFPs and more",
    icon: AgentDraftingAssistantSvg,
    // TODO: Temporary disable flag - remove this line below to re-enable
    isDisabled: false,
    tooltip:
      "Perfect when:\n" +
      "• Creating/Drafting documents (vs just analyzing existing ones)\n" +
      "• Assembling RFPs, SOWs, or bid packages based on requirements\n" +
      "• Refine (or add to) documents with custom instructions\n" +
      "• Drafting approval documents for stakeholder alignment\n" +
      "• Need help structuring any CCM/procurement document",
  },
];

// Component that uses the hooks - now receives props for state management
interface AppContentProps {
  recommendedAgentId: string | null;
  setRecommendedAgentId: (id: string | null) => void;
}

const AppContent: React.FC<AppContentProps> = ({ recommendedAgentId, setRecommendedAgentId }) => {
  // Access session context to manage contract type and to know whether a
  // stored conversation was restored on this launch.
  const {
    setContractType,
    getContractType,
    isSessionRestorePending,
    hasRestoredSession,
    showRestoreNotice,
    dismissRestoreNotice,
  } = useSession();

  // Access auth context to get access token and loading state
  const {
    accessToken,
    userInfo,
    isLoading: authLoading,
    error: authError,
    authCompleted,
    allowFallback,
    accessDecision,
    canAccessBackend,
    retrySignIn,
  } = useAuth();

  // Bootstrap is incomplete until either:
  //   (a) auth completed without a token (degraded / fallback path — no
  //       /users/me to call), OR
  //   (b) auth completed AND the access-gate decision is back.
  // This is what holds the loading spinner up so unprovisioned users never
  // see the Welcome screen flash before the deny banner arrives.
  const accessCheckPending = accessToken !== null && accessDecision === null;

  // MODIFIED: Now also getting currentAgentFeatures for file upload configuration
  const { currentAgent, setCurrentAgentById, currentAgentFeatures } = useAgent();

  // Document hooks
  const documentContext = useDocumentContext();
  const documentClassification = useDocumentClassification();

  // Track Changes auto-enable on launch
  const { notification: trackChangesNotification, dismissNotification: dismissTrackChanges } =
    useTrackChangesAutoEnable();

  // Track Changes revert notice (published from AutoRedlinePreview when an
  // edit-time revert occurs; rendered as a top-of-taskpane banner below).
  const { notice: trackChangesRevertNotice, dismissNotice: dismissTrackChangesRevertNotice } =
    useTrackChangesRevertNotice();

  // Add a ref to track if document is being fetched to prevent concurrent Word API calls
  const isFetchingDocumentRef = React.useRef(false);

  // File upload state, lifted to App level so the drag-and-drop overlay and the
  // chat panel share one source of truth.
  const fileUpload = useFileUpload(
    (files) => {
      // When files change, update the model service
      if (currentAgentFeatures.allowFileUpload) {
        const modelService = agentModelServiceFactory.getServiceForAgent(currentAgent.id);
        if (modelService && "setAdditionalFiles" in modelService) {
          (modelService as AgentAwareModelService).setAdditionalFiles(files);
          log.info("Updated model service with uploaded files from App level", {
            fileCount: files.length,
            fileNames: files.map((f) => f.name),
            agentId: currentAgent.id,
          });
        }
      }
    },
    {
      // Pass agent-specific validation configuration
      supportedFileTypes: currentAgentFeatures.supportedFileTypes,
      maxFileSizeMB: currentAgentFeatures.maxFileSizeMB,
      maxFileCount: currentAgentFeatures.maxFileCount,
    }
  );

  // NEW: Drag and drop functionality for the entire application
  const { isDragging, dragHandlers, manualClose } = useDragDropZone({
    onFilesDropped: (files) => {
      // Validate files using existing validation
      const validationResults = validateFiles(files, fileUpload.files.length, {
        supportedFileTypes: currentAgentFeatures.supportedFileTypes,
        maxFileSizeMB: currentAgentFeatures.maxFileSizeMB,
        maxFileCount: currentAgentFeatures.maxFileCount,
      });

      // Check for validation errors
      const failedValidation = validationResults.find((result) => !result.isValid);
      if (failedValidation) {
        log.warn("Dropped files validation failed", {
          error: failedValidation.error,
          errorCode: failedValidation.errorCode,
          droppedFileCount: files.length,
        });
        // The fileUpload hook will handle showing the error
        fileUpload.addFiles(files, "drag_drop"); // Let the hook handle the error display
        return;
      }

      // Add valid files
      log.info("Processing dropped files", {
        count: files.length,
        fileNames: files.map((f) => f.name),
        agentId: currentAgent.id,
      });
      fileUpload.addFiles(files, "drag_drop");
    },
    isEnabled: currentAgentFeatures.allowFileUpload,
  });

  // Get agent-specific message for the overlay
  const getDropMessage = () => {
    if (currentAgent.id === "contract-qa") {
      return {
        message: "Drop template files here",
        subMessage: ".docx files with standard positions",
      };
    } else if (currentAgent.id === "drafting-assistant") {
      return {
        message: "Drop context documents here",
        subMessage: "Upload specs, SOW draft & Scope",
      };
    } else if (currentAgent.id === "red-line-analyzer") {
      return {
        message: "Drop contract files here",
        subMessage: "Word, Excel or PDF documents",
      };
    }
    return {
      message: "Drop your files here",
      subMessage: currentAgentFeatures.supportedFileTypes?.join(", ") || "Supported files",
    };
  };

  // Track previous agent to detect changes
  // Files are now preserved when switching agents for better UX
  const previousAgentRef = React.useRef(currentAgent.id);

  React.useEffect(() => {
    // Check if the agent has actually changed (not just initial mount)
    if (previousAgentRef.current && currentAgent.id !== previousAgentRef.current) {
      log.info("Agent changed at App level, preserving files", {
        from: previousAgentRef.current,
        to: currentAgent.id,
        preservedFileCount: fileUpload.files.length,
      });

      // Files are now preserved across agent switches
      // This improves UX by maintaining user context

      // Sync contract type to the new agent's model service
      const currentContractType = getContractType();
      if (currentContractType) {
        const modelService = agentModelServiceFactory.getServiceForAgent(currentAgent.id);
        if (modelService && "setContractType" in modelService) {
          (modelService as AgentAwareModelService).setContractType(currentContractType);
          log.info("Contract type synced to new agent", {
            contractType: currentContractType,
            agentId: currentAgent.id,
          });
        }
      }

      // Sync uploaded files to the new agent's model service (respecting file count limits)
      if (fileUpload.files.length > 0 && currentAgentFeatures.allowFileUpload) {
        const modelService = agentModelServiceFactory.getServiceForAgent(currentAgent.id);
        if (modelService && "setAdditionalFiles" in modelService) {
          // Extract the actual File objects from UploadedFile wrappers
          const allFiles = fileUpload.files.map((f) => f.file);

          // Respect the agent's maxFileCount limit
          const maxFiles = currentAgentFeatures.maxFileCount || 1;
          const filesToSync = allFiles.slice(0, maxFiles);

          // Warn if files were truncated
          if (allFiles.length > maxFiles) {
            log.warn("File count exceeds agent limit, truncating", {
              agentId: currentAgent.id,
              totalFiles: allFiles.length,
              maxAllowed: maxFiles,
              syncedFiles: filesToSync.length,
              truncatedFiles: allFiles.slice(maxFiles).map((f) => f.name),
            });
          }

          (modelService as AgentAwareModelService).setAdditionalFiles(filesToSync);
          log.info("Files synced to new agent", {
            fileCount: filesToSync.length,
            totalAvailable: allFiles.length,
            maxAllowed: maxFiles,
            fileNames: filesToSync.map((f) => f.name),
            agentId: currentAgent.id,
          });
        }
      }
    }

    // Update our tracking reference for the next change
    previousAgentRef.current = currentAgent.id;
  }, [
    currentAgent.id,
    getContractType,
    currentAgentFeatures.allowFileUpload,
    currentAgentFeatures.maxFileCount,
  ]);

  // Sync files on initial mount or when files change (separate from agent switching)
  React.useEffect(() => {
    // Only sync if we have files and the agent supports file uploads
    if (fileUpload.files.length > 0 && currentAgentFeatures.allowFileUpload) {
      const modelService = agentModelServiceFactory.getServiceForAgent(currentAgent.id);
      if (modelService && "setAdditionalFiles" in modelService) {
        const allFiles = fileUpload.files.map((f) => f.file);
        const maxFiles = currentAgentFeatures.maxFileCount || 1;
        const filesToSync = allFiles.slice(0, maxFiles);

        (modelService as AgentAwareModelService).setAdditionalFiles(filesToSync);
        log.debug("Files synced on mount/update", {
          fileCount: filesToSync.length,
          maxAllowed: maxFiles,
          agentId: currentAgent.id,
        });
      }
    }
  }, [
    fileUpload.files,
    currentAgent.id,
    currentAgentFeatures.allowFileUpload,
    currentAgentFeatures.maxFileCount,
  ]);

  // Cleanup all agent services when the app unmounts to prevent memory leaks
  React.useEffect(() => {
    return () => {
      log.info("App unmounting, cleaning up all agent services");
      agentModelServiceFactory.cleanupAll();
    };
  }, []); // Empty dependency array means this only runs on mount/unmount

  const documentError = documentContext.error;

  // Auth is the only remaining source of app-level errors, and AuthContext
  // exposes no way to clear one, so dismissal is tracked here.
  const [dismissedAppError, setDismissedAppError] = React.useState<string | null>(null);
  const error = authError && authError !== dismissedAppError ? authError : null;

  // Re-arm the banner whenever a fresh auth attempt starts. AuthContext clears
  // its error before re-running the flow, so a null is the signal that a new
  // attempt is underway. Without this, dismissing the banner would silence it
  // permanently: the auth layer emits one deterministic message per platform,
  // so a failed "Retry sign-in" reproduces the identical string and would be
  // swallowed as already-dismissed, leaving the user no feedback at all.
  React.useEffect(() => {
    if (authError === null) setDismissedAppError(null);
  }, [authError]);

  // Dismiss handlers for error displays
  const handleDismissDocumentError = () => {
    documentContext.resetError();
  };

  const handleDismissAppError = () => {
    setDismissedAppError(authError);
  };

  // Reset classification ONLY on a real account switch — previously this
  // re-fired on every auto-refresh (every ~59 minutes) because accessToken is
  // a fresh string even when the identity is unchanged, forcing an unnecessary
  // re-classification of the same document.
  const previousEmailRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!authCompleted || !canAccessBackend) return;
    const email = userInfo?.email ?? null;
    const prev = previousEmailRef.current;
    previousEmailRef.current = email;
    if (!prev) return; // first successful auth — don't reset; let initial classification run
    if (prev.toLowerCase() === (email ?? "").toLowerCase()) return;
    documentClassification.resetClassification();
    log.info("Classification state reset due to account switch");
  }, [authCompleted, canAccessBackend, userInfo?.email]);

  // Auto-classify document on mount - wait for authentication to complete or allow fallback
  React.useEffect(() => {
    const performInitialClassification = async () => {
      // Wait for authentication process to complete (either success or fallback enabled)
      if (!authCompleted) {
        log.debug("Waiting for authentication to complete before document classification");
        return;
      }

      // Skip if already classified
      if (documentClassification.hasClassified) {
        log.debug("Skipping classification - already classified");
        return;
      }

      // Wait for the launch-time session restore to settle, then leave a
      // restored conversation alone: classification would pick the agent for
      // whatever document happens to be open and switch the user away from the
      // conversation they just came back to.
      if (isSessionRestorePending) {
        log.debug("Waiting for session restore before document classification");
        return;
      }
      if (hasRestoredSession) {
        log.info("Skipping classification - a stored conversation was restored");
        return;
      }

      // Fail-open sessions (auth completed but no token) must not fire the
      // classification call: without an Authorization header it is rejected
      // as a strict-mode 401 ERROR in the backend logs while returning
      // nothing the user can see. (The old `!accessToken && !allowFallback`
      // guard never blocked here, because fail-open sets allowFallback=true.)
      if (!canAccessBackend) {
        log.debug("Backend not accessible (no token) - skipping classification");
        return;
      }

      try {
        log.info("Performing initial document classification", {
          hasAccessToken: !!accessToken,
          allowFallback,
          authCompleted,
          hasClassified: documentClassification.hasClassified,
        });

        let ooxmlContent = documentContext.documentOoxml;

        // If we don't have document content yet, fetch it
        if (!ooxmlContent && !isFetchingDocumentRef.current) {
          isFetchingDocumentRef.current = true;
          try {
            log.info("Fetching document content for classification");
            ooxmlContent = await documentContext.getDocumentOoxml();
          } catch (docError) {
            log.error("Failed to fetch document content", {
              error: docError instanceof Error ? docError.message : String(docError),
            });
          } finally {
            isFetchingDocumentRef.current = false;
          }
        }

        // If we successfully got document content, classify it
        if (ooxmlContent) {
          const { agentId, contractType } =
            await documentClassification.classifyDocumentContent(ooxmlContent);

          if (agentId) {
            // Store in session context FIRST (before agent state changes)
            if (contractType) {
              setContractType(contractType);
              log.info("Contract type stored in session from classification", {
                contractType,
                agentId,
              });
            }

            // Now update agent state
            setRecommendedAgentId(agentId);
            setCurrentAgentById(agentId);

            // Sync contract type to model service AFTER agent is set
            if (contractType) {
              const modelService = agentModelServiceFactory.getServiceForAgent(agentId);
              if (modelService && "setContractType" in modelService) {
                (modelService as AgentAwareModelService).setContractType(contractType);
                log.info("Contract type synced to model service", {
                  contractType,
                  agentId,
                });
              }
            }

            log.info("Initial classification complete", {
              recommendedAgentId: agentId,
              contractType: contractType,
              authMode: accessToken ? "authenticated" : "fallback",
            });
          }
        } else {
          log.warn(
            "Could not get document content for classification - document may be empty or inaccessible"
          );
        }
      } catch (error) {
        log.error("Failed to perform initial classification", {
          error: error instanceof Error ? error.message : String(error),
          hasToken: !!accessToken,
          allowFallback,
        });
      }
    };

    performInitialClassification();
  }, [
    authCompleted,
    canAccessBackend,
    accessToken,
    allowFallback,
    documentClassification.hasClassified,
    isSessionRestorePending,
    hasRestoredSession,
  ]); // Re-run when auth completes or classification state changes

  // Hold the pane while stored conversations are being read, so the chat never
  // paints its empty welcome screen and then flips into a conversation. Denied
  // users are exempt: their blocking screen is the next branch down, and no
  // restore is attempted for them.
  const restorePending = isSessionRestorePending && accessDecision?.kind !== "denied";

  // Show authentication loading screen
  if ((authLoading && !authCompleted) || accessCheckPending || restorePending) {
    return (
      <main
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Authenticating with Contract.Space"
        style={{
          padding: 20,
          fontFamily: "'Segoe UI', sans-serif",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: 20 }}>
          <div
            aria-hidden="true"
            style={{
              width: 40,
              height: 40,
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #0078d4",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <h3 style={{ color: "#323130", margin: "0 0 8px 0" }}>Connecting to Contract.Space</h3>
          <p style={{ color: "#605e5c", margin: 0, fontSize: 14 }}>
            {authCompleted && !accessCheckPending
              ? "Restoring your conversations..."
              : "Authenticating with your Office 365 account..."}
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </main>
    );
  }

  // Access-gate deny: route to the matching full-pane blocking screen
  // before any chat / nav UI mounts. The five gate deny statuses collapse
  // into the two ready screens by who needs to act to resolve it:
  //   no_org / suspended           → BlockingNoOrgScreen   (org-level fix)
  //   no_user / inactive_user /
  //     identity_mismatch          → BlockingNoUserScreen  (user-account fix)
  // `unknown` (network / 401 / unexpected) intentionally falls through to
  // the existing UI — fail open rather than hard-blocking on a flaky
  // backend response that may resolve on the next call.
  if (accessDecision?.kind === "denied") {
    const orgLevelDeny =
      accessDecision.status === "no_org" || accessDecision.status === "suspended";
    return orgLevelDeny ? <BlockingNoOrgScreen /> : <BlockingNoUserScreen />;
  }

  // Degraded state: auth completed but no usable token. Offer a retry instead
  // of silently dropping every backend call to a 401. The pane still renders
  // and local, document-only work keeps functioning because `allowFallback` is
  // true downstream — but we surface the retry path prominently so users
  // aren't stuck.
  const showRetryBanner = authCompleted && !canAccessBackend;

  return (
    <>
      {/* NEW: Drag and drop overlay - shows when dragging files */}
      <DragDropOverlay
        isVisible={isDragging}
        message={getDropMessage().message}
        subMessage={getDropMessage().subMessage}
        onClose={manualClose}
      />

      {/* Main container for the entire app - now with drag handlers */}
      <main
        {...dragHandlers}
        style={{
          padding: 8,
          fontFamily: "'Segoe UI', sans-serif",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          boxSizing: "border-box",
        }}
      >
        {/* Licence expiry countdown. First in the pane and above every other
            banner: it is permanent and cannot be dismissed, so anything
            transient belongs underneath it. Renders nothing for tenants with no
            licence notice, which is everyone by default. */}
        <LicenceExpiryBanner />

        {/* Track Changes notification */}
        {trackChangesNotification && (
          <div style={{ margin: "0 0 12px 0" }}>
            <InfoBanner
              message={trackChangesNotification.message}
              variant={trackChangesNotification.variant}
              autoDismissMs={8000}
              onDismiss={dismissTrackChanges}
            />
          </div>
        )}

        {/* Restored-conversation notice. Raised once per launch by the session
            layer, not per agent, so switching between restored agents does not
            show it again. Says what the restore could not carry: the files, and
            the link to a particular document. */}
        {showRestoreNotice && (
          <div style={{ margin: "0 0 12px 0" }}>
            <InfoBanner
              message="Picked up where you left off. This conversation isn't linked to the document you have open, and any files attached earlier are no longer available."
              variant="info"
              autoDismissMs={10000}
              onDismiss={dismissRestoreNotice}
            />
          </div>
        )}

        {/* Track Changes revert notice — published by AutoRedlinePreview when
            the user toggled Track Changes between preview and apply and we
            restored it to the captured mode before running the edits. */}
        {trackChangesRevertNotice && (
          <div style={{ margin: "0 0 12px 0" }}>
            <InfoBanner
              message={trackChangesRevertNotice.message}
              variant="info"
              autoDismissMs={12000}
              onDismiss={dismissTrackChangesRevertNotice}
            />
          </div>
        )}

        {/* Document error message */}
        {documentError && (
          <div style={{ margin: "0 0 12px 0" }}>
            <ErrorMessage error={documentError} onDismiss={handleDismissDocumentError} />
          </div>
        )}

        {/* General error message (auth only, since the tabs were retired) */}
        {error && !documentError && (
          <div style={{ margin: "0 0 12px 0" }}>
            <ErrorMessage error={error} onDismiss={handleDismissAppError} />
          </div>
        )}

        {/* Degraded-auth retry banner — only shown when we finished the auth
            flow but have no token. Gives users a recovery path that doesn't
            require closing and reopening the task pane. */}
        {showRetryBanner && (
          <div
            role="status"
            aria-live="polite"
            style={{
              margin: "0 0 12px 0",
              padding: "10px 12px",
              backgroundColor: "#fff4ce",
              border: "1px solid #f3d37a",
              borderRadius: 4,
              fontSize: 13,
              color: "#323130",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span>You're not signed in. Some features are unavailable.</span>
            <button
              onClick={() => {
                log.info("User clicked retry sign-in");
                retrySignIn();
              }}
              style={{
                flexShrink: 0,
                padding: "4px 12px",
                backgroundColor: "#0078d4",
                color: "white",
                border: "none",
                borderRadius: 2,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Retry sign-in
            </button>
          </div>
        )}

        {/* This div will take the remaining vertical space */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <StreamingChatPanel fileUpload={fileUpload} />
        </div>
      </main>
    </>
  );
};

// The main App component with providers - ENHANCED with AgentProvider
const App: React.FC = () => {
  // State to track recommended agent from classification
  const [recommendedAgentId, setRecommendedAgentId] = React.useState<string | null>(null);

  React.useEffect(() => {
    log.info("App initialized with enhanced provider hierarchy", {
      providers: [
        "AuthProvider",
        "AgentProvider",
        "SessionProvider",
        "ModelProvider",
        "ChatSettingsProvider",
      ],
      timestamp: new Date().toISOString(),
    });
  }, []);

  return (
    // Provider hierarchy: Order matters here!
    // ErrorBoundary wraps everything so any rendering failure in a provider or
    // the tree below gets captured in telemetry instead of blanking the taskpane.
    <ErrorBoundary name="root">
      {/* AuthProvider: Authentication context (outermost) */}
      <AuthProvider>
        {/* AgentProvider: MUST come before SessionProvider to allow session to access agent state */}
        <PostHogProvider client={posthogClient}>
          <AgentProvider
            availableAgents={AVAILABLE_AGENTS}
            initialAgentId={recommendedAgentId || "red-line-analyzer"}
          >
            {/* SessionProvider: Now agent-aware - manages conversation history per agent */}
            <SessionProvider>
              {/* ModelProvider: Can access both AgentContext and SessionContext */}
              <ModelProvider>
                {/* ChatSettingsProvider: Manages user preferences */}
                <ChatSettingsProvider>
                  <AppContent
                    recommendedAgentId={recommendedAgentId}
                    setRecommendedAgentId={setRecommendedAgentId}
                  />
                </ChatSettingsProvider>
              </ModelProvider>
            </SessionProvider>
          </AgentProvider>
        </PostHogProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

// Initialize the app
Office.onReady(() => {
  createRoot(document.getElementById("root")!).render(<App />);
});

export default App;
